/**
 * ListSettings - управление списком повторяющихся элементов
 * Используется для: features items, gallery images, FAQ items, etc.
 */
import React from 'react';
import Icon from '../../../components/AppIcon';
import FieldRenderer from './FieldRenderer';
import SettingsGroup from './SettingsGroup';

export default function ListSettings({
  label,
  value = [],
  onChange,
  itemFields = [],
  addLabel = 'Добавить',
  itemLabel = 'Элемент',
  maxItems,
  allSections,
  allPages,
  projectId,
}) {
  const items = value || [];

  const addItem = () => {
    if (maxItems && items.length >= maxItems) return;

    // Создаём новый элемент с дефолтными значениями
    const newItem = {
      id: Date.now().toString(),
    };
    itemFields.forEach(field => {
      if (field.default !== undefined) {
        newItem[field.key] = field.default;
      } else if (field.type === 'text') {
        newItem[field.key] = { text: '' };
      } else {
        newItem[field.key] = '';
      }
    });

    onChange([...items, newItem]);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;

    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    onChange(newItems);
  };

  const updateItem = (index, fieldKey, fieldValue) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [fieldKey]: fieldValue };
    onChange(newItems);
  };

  const canAdd = !maxItems || items.length < maxItems;

  return (
    <SettingsGroup
      title={`${label} (${items.length})`}
      rightAction={
        canAdd && (
          <button
            onClick={addItem}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            type="button"
          >
            <Icon name="Plus" size={12} />
            <span>{addLabel}</span>
          </button>
        )
      }
    >
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={item.id || index}
            className="p-3 bg-muted rounded-lg space-y-3"
          >
            {/* Item header */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-text-secondary font-medium">
                {itemLabel} {index + 1}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  className="p-1 hover:bg-background rounded disabled:opacity-30 transition-colors"
                  type="button"
                >
                  <Icon name="ChevronUp" size={14} />
                </button>
                <button
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  className="p-1 hover:bg-background rounded disabled:opacity-30 transition-colors"
                  type="button"
                >
                  <Icon name="ChevronDown" size={14} />
                </button>
                <button
                  onClick={() => removeItem(index)}
                  className="p-1 hover:bg-error/10 hover:text-error rounded transition-colors"
                  type="button"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>

            {/* Item fields */}
            <div className="space-y-3">
              {itemFields.map(field => (
                <FieldRenderer
                  key={field.key}
                  field={field}
                  value={item[field.key]}
                  onChange={(v) => updateItem(index, field.key, v)}
                  allSections={allSections}
                  allPages={allPages}
                  projectId={projectId}
                />
              ))}
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-4">
            Нет элементов. Нажмите "{addLabel}" чтобы добавить.
          </p>
        )}
      </div>
    </SettingsGroup>
  );
}
